import ExcelJS from 'exceljs';
import { translations } from '../i18n/translations';
import { getFullFileName } from './fileOperations';
import { calculateSiglatureAndMorsetti } from './quantities';
import { getSiglaturaPerStampa, replaceArrowsForPDF, formatSezione } from './cableLogic';
import { extractSiglaturaFromCollegamento } from './acPowerLogic';

// ==================== FUNZIONI DI ESPORTAZIONE SIGLATURA ====================

export const exportSiglature = async (cables, settings, setWarnings, setSuccessMessage) => {
    const language = settings?.lingua || 'it';
    const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
            if (text && typeof text === 'object' && k in text) {
                text = text[k];
            } else {
                return key;
            }
        }
        if (typeof text === 'string' && params) {
            return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
    };

    if (!settings.fileName || settings.fileName.trim() === '') {
        setWarnings(prev => [
            ...prev,
            t('fileOperations.noFileNameWarning')
        ]);
        return;
    }

    try {
        // VALIDA I CAVI E GENERA WARNING PER CAMPI MANCANTI
        const { validateCableFields } = await import('./validation');
        const cablesWithMissingFields = [];
        
        cables.forEach((cable, index) => {
            const missingFields = validateCableFields(cable, cables, settings.lingua);
            if (missingFields.length > 0) {
                cablesWithMissingFields.push({
                    index: index + 1,
                    missingFields: missingFields
                });
            }
        });
        
        if (cablesWithMissingFields.length > 0) {
            const warningMessages = cablesWithMissingFields.map(cable => 
                `Cavo #${cable.index}: ${cable.missingFields.join(', ')}`
            );
            setWarnings(prev => [
                ...prev,
                t('fileOperations.cablesWithMissingFields', { count: cablesWithMissingFields.length, fields: warningMessages.join('; ') })
            ]);
        }

        // OTTIENI I DATI CALCOLATI
        const allResults = calculateSiglatureAndMorsetti(cables, settings);

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Siglature');

        // ==================== HEADER PRINCIPALE (GRIGIO) ====================

        // Genera i codici
        const headerCodes = generateHeaderCodes(settings.fileName);
        const revisionFormatted = settings.revision.toString().padStart(2, '0');

        // Prima riga header - ALTEZZA 38.25
        sheet.mergeCells('A1:C1');
        sheet.getCell('A1').value = headerCodes.eleqe; // ELEQExxxxx
        sheet.getCell('A1').font = { bold: true, size: 16 };
        sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('D1:F1');
        sheet.getCell('D1').value = headerCodes.elent; // ELENTxxxxx
        sheet.getCell('D1').font = { bold: true, size: 16 };
        sheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('D1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('G1:I1');
        sheet.getCell('G1').value = settings.installazione || 'AT0700ZM00'; // Campo Installazione
        sheet.getCell('G1').font = { bold: true, size: 16 };
        sheet.getCell('G1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('G1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getRow(1).height = 38.25;

        // Seconda riga header - ALTEZZA 20.25
        sheet.mergeCells('A2:F2');
        sheet.getCell('A2').value = headerCodes.elecb; // ELECBxxxxx (nome file)
        sheet.getCell('A2').font = { bold: true, size: 14 };
        sheet.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('A2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('G2:H2');
        sheet.getCell('G2').value = settings.schemaElettrico || 'SCE-15032488'; // Campo Schema Elettrico
        sheet.getCell('G2').font = { bold: true, size: 12 };
        sheet.getCell('G2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('G2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getCell('I2').value = `Rev. ${revisionFormatted}`; // Revisione dalle impostazioni
        sheet.getCell('I2').font = { bold: true, size: 12 };
        sheet.getCell('I2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('I2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getRow(2).height = 20.25;

        let currentRow = 4;

        // ==================== SEZIONE COMPONENTI ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = 'COMPONENTI';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            'SIGLA\nCOMPONENTE',
            'Targhette FLAT\n4x15\nELEGM45011',
            allResults.sectionAbove6,
            { ext: 'EXT', qe: 'QE', sw: 503, tipo: '42092B' }
        );

        // ==================== SEZIONE MORSETTI ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = 'MORSETTI';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            'SIGLA MORSETTO',
            'Targh x mors\nELEGM45021',
            allResults.morsetti,
            { ext: 'QE', qe: '', sw: 42, tipo: '41390N' }
        );

        // ==================== SEZIONE INTERRUTTORI E SPIE ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = 'INTERRUTTORI E SPIE';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            '15X27\nTARGHETTE',
            'Targh. 15x27\nELEGM45000',
            [], // Nessun dato automatico per ora
            { ext: 'EXT', qe: '', sw: 166, tipo: '47091N' },
            '[TARGHETTA]', '[SIGLA]'
        );

        // ==================== SEZIONE 2,5-6,0 MMQ ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = '2,5-6,0 MMQ SIGLE X CAVI';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            '2,5-6,0 MMQ\nSIGLA FILO',
            'Quantità\nELEGM45005',
            allResults.section25_6,
            { ext: 'QE', qe: '', sw: 122, tipo: '40290' }
        );

        // ==================== SEZIONE 0,5-1,5 MMQ ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = '0,5-1,5 MMQ SIGLE X CAVI';
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            '0,5-1,5 MMQ\nSIGLA FILO',
            'Quantità\nELEGM45002',
            allResults.section05_15,
            { ext: 'QE', qe: '', sw: 121, tipo: '40190' }
        );

        // ==================== FORMATTAZIONE FINALE ====================

        sheet.columns = [
            { width: 18 }, { width: 12 }, { width: 8 }, { width: 16 }, { width: 8 },
            { width: 12 }, { width: 15 }, { width: 15 }, { width: 15 }
        ];

        // Applica bordi a tutte le celle
        const lastRow = currentRow;
        for (let row = 1; row <= lastRow; row++) {
            const riga = sheet.getRow(row);
            const hasContent = riga.values.some((val, idx) =>
                idx >= 1 && idx <= 9 && val !== null && val !== undefined && val !== ''
            );

            if (hasContent) {
                for (let col = 1; col <= 9; col++) {
                    const cell = riga.getCell(col);
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    if (!cell.alignment) {
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'center',
                            wrapText: true
                        };
                    }
                }
            }
        }

        // Salva il file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fullFileName = getFullFileName(settings, 'xlsx', false, false, false).replace('.xlsx', '-siglature.xlsx');

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fullFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSuccessMessage(t('fileOperations.siglatureExportedSuccess', { name: fullFileName }));
        setTimeout(() => setSuccessMessage(''), 5000);

    } catch (error) {
        console.error('Errore durante l\'esportazione siglature:', error);
        setWarnings(prev => [
            ...prev,
            t('fileOperations.exportSiglatureError', { error: error.message || error.toString() })
        ]);
    }
};

// ==================== FUNZIONE HELPER PER CREARE SEZIONI ====================
const createSectionWithData = (sheet, startRow, titleCol1, titleCol9, data, config, placeholderCol1 = '[SIGLA]', placeholderCol4 = '[SIGLA]') => {
    let currentRow = startRow;

    const headerRow = currentRow;
    const subHeaderRow = currentRow + 1;

    // MERGE VERTICALI
    sheet.mergeCells(`A${headerRow}:C${headerRow}`);
    sheet.mergeCells(`D${headerRow}:D${subHeaderRow}`);
    sheet.mergeCells(`E${headerRow}:E${subHeaderRow}`);
    sheet.mergeCells(`F${headerRow}:F${subHeaderRow}`);

    // Headers
    sheet.getCell(`A${headerRow}`).value = titleCol1;
    sheet.getCell(`A${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`A${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`A${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`D${headerRow}`).value = 'QE = Quadro el\nEXT = x Refrind';
    sheet.getCell(`D${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`D${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`D${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`E${headerRow}`).value = 'SW';
    sheet.getCell(`E${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`E${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`E${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`F${headerRow}`).value = 'TIPO';
    sheet.getCell(`F${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`F${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`F${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    // Colonne G e H vuote
    ['G', 'H'].forEach(col => {
        sheet.getCell(`${col}${headerRow}`).value = '';
        sheet.getCell(`${col}${headerRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    sheet.getCell(`I${headerRow}`).value = titleCol9;
    sheet.getCell(`I${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`I${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`I${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getRow(headerRow).height = 35;

    // Sottotitoli
    ['INIZIO', 'RIPETIZIONI', 'FINE'].forEach((label, idx) => {
        const cell = sheet.getCell(subHeaderRow, idx + 1);
        cell.value = label;
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    // Colonne G e H vuote nel subheader
    ['G', 'H'].forEach(col => {
        sheet.getCell(`${col}${subHeaderRow}`).value = '';
        sheet.getCell(`${col}${subHeaderRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    // CALCOLA E INSERISCI TOTALE
    const totalQuantity = data.length > 0
        ? data.reduce((sum, item) => sum + item.quantita, 0)
        : 1;

    sheet.getCell(`I${subHeaderRow}`).value = totalQuantity;
    sheet.getCell(`I${subHeaderRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`I${subHeaderRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`I${subHeaderRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' } // GIALLO per totale
    };

    currentRow = subHeaderRow + 1;

    // INSERISCI DATI REALI
    if (data.length > 0) {
        data.forEach(item => {
            const row = sheet.getRow(currentRow);
            row.values = [
                item.inizio,
                item.ripetizioni,
                item.fine || '',
                config.ext,
                config.sw,
                config.tipo,
                '',
                '',
                item.quantita
            ];
            row.eachCell((cell, colNumber) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                if (colNumber === 9) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFD3D3D3' }
                    };
                }
            });
            currentRow++;
        });
    } else {
        // Riga placeholder se non ci sono dati
        const row = sheet.getRow(currentRow);
        row.values = [placeholderCol1, 1, '', config.ext, config.sw, config.tipo, '', '', 1];
        row.eachCell((cell, colNumber) => {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: colNumber === 9 ? { argb: 'FFD3D3D3' } : { argb: 'FFF0F0F0' }
            };
        });
        currentRow++;
    }

    return currentRow + 1;
};

// Funzione helper per generare i codici dell'header
const generateHeaderCodes = (fileName) => {
    if (!fileName) {
        return {
            eleqe: 'ELEQExxxxx',
            elent: 'ELENTxxxxx',
            elecb: 'ELECBxxxxx'
        };
    }

    // Estrai la parte numerica
    let numericPart = '';

    if (fileName.match(/^ELECB\d{5}$/i)) {
        // Se è nel formato ELECBxxxxx, estrai le 5 cifre
        numericPart = fileName.slice(-5);
    } else if (fileName.match(/\d{5}/)) {
        // Se contiene 5 cifre consecutive, usale
        const match = fileName.match(/\d{5}/);
        numericPart = match[0];
    } else {
        // Fallback
        numericPart = 'xxxxx';
    }

    return {
        eleqe: `ELEQE${numericPart}`,
        elent: `ELENT${numericPart}`,
        elecb: fileName.startsWith('ELECB') ? fileName : `ELECB${numericPart}`
    };
};

// Funzione per caricare le librerie PDF
const loadPDFLibraries = async () => {
    try {
        if (window.jspdf && window.svg2pdf) {
            return true;
        }
        
        // Carica jsPDF se non è già caricato
        if (!window.jspdf) {
            const jsPDFScript = document.createElement('script');
            jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            jsPDFScript.async = true;
            
            await new Promise((resolve, reject) => {
                jsPDFScript.onload = () => resolve();
                jsPDFScript.onerror = () => reject(new Error('Errore caricamento jsPDF'));
                document.head.appendChild(jsPDFScript);
            });
        }
        
        // Carica svg2pdf.js se non è già caricato
        if (!window.svg2pdf) {
            const svg2pdfScript = document.createElement('script');
            svg2pdfScript.src = 'https://unpkg.com/svg2pdf.js@2.2.1/dist/svg2pdf.umd.min.js';
            svg2pdfScript.async = true;
            
            await new Promise((resolve, reject) => {
                svg2pdfScript.onload = resolve;
                svg2pdfScript.onerror = reject;
                document.head.appendChild(svg2pdfScript);
            });
        }
        
        return true;
    } catch (error) {
        console.error('Errore nel caricamento delle librerie PDF:', error);
        return false;
    }
};

const exportSchematicToPDFBase = async ({
    cables,
    settings,
    setWarnings,
    setSuccessMessage,
    setDynamicGroupsDivision = null,
    debugMode = false,
    groupSelector
}) => {
    const language = settings?.lingua || 'it';
    const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
            if (text && typeof text === 'object' && k in text) {
                text = text[k];
            } else {
                return key;
            }
        }
        if (typeof text === 'string' && params) {
            return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
    };

    const originalDynamicGroupsDivision = settings.dynamicGroupsDivision;
    const cableIndexMap = new Map();
    cables.forEach((cable, index) => {
        if (cable.originalIndex !== undefined) {
            cableIndexMap.set(cable.originalIndex, cable);
        }
    });

    settings.dynamicGroupsDivision = true;
    settings.debugMode = debugMode;

    if (setDynamicGroupsDivision) {
        setDynamicGroupsDivision(true);
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    try {
        const librariesLoaded = await loadPDFLibraries();
        if (!librariesLoaded) {
            setWarnings(prev => [...prev, t('fileOperations.pdfLibrariesError')]);
            return;
        }


        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const usableWidth = pageWidth - (2 * margin);
        let currentY = margin;

        // Colori del tema
        const primaryColor = [59, 130, 246]; // Blue-600
        const secondaryColor = [99, 102, 241]; // Indigo-600
        const grayColor = [107, 114, 128]; // Gray-500
        const lightGrayColor = [229, 231, 235]; // Gray-200

        // HEADER COLORATO
        pdf.setFillColor(...primaryColor);
        pdf.rect(0, 0, pageWidth, 35, 'F');

        // Genera nome file se non fornito
        const fileName = settings.fileName || 'Progetto senza nome';

        // Titolo principale
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text('Schema Cablaggi Elettrici', margin, 15);

        // Sottotitolo con revisione
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const revisionFormatted = settings.revision ? settings.revision.toString().padStart(2, '0') : '00';
        pdf.text(`${fileName} - Rev. ${revisionFormatted}`, margin, 23);

        // Box informazioni
        currentY = 40;
        pdf.setFillColor(...lightGrayColor);
        pdf.roundedRect(margin, currentY, usableWidth, 15, 3, 3, 'F');

        // Info nel box
        pdf.setFontSize(10);
        pdf.setTextColor(...grayColor);
        const infoY = currentY + 6;

        pdf.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, margin + 5, infoY);
        pdf.text(`Ora: ${new Date().toLocaleTimeString('it-IT')}`, margin + 45, infoY);
        pdf.text(`Totale cavi: ${cables.length}`, margin + 85, infoY);
        pdf.text('Sistema: EW-3035+L', margin + 125, infoY);

        currentY += 20;

        const elementsToHide = [];
        document.querySelectorAll('.p-2.text-blue-600, .p-2.text-red-600').forEach(el => {
            elementsToHide.push({ element: el, display: el.style.display });
            el.style.display = 'none';
        });

        const cableGroups = document.querySelectorAll(groupSelector);

        for (let i = 0; i < cableGroups.length; i++) {
            const group = cableGroups[i];
            const titleElement = group.querySelector('h3.text-lg.font-semibold.text-gray-800');
            const actualTitle = titleElement ? titleElement.textContent.trim() : `Gruppo ${i + 1}`;
            
            // Ottieni il codice del gruppo dai cavi
            let groupCode = '';
            const firstCableElement = group.querySelector('div[class*="border-2"][class*="rounded-lg"] span');
            const datasetGroupCode = group.dataset?.groupCode;
            if (datasetGroupCode) {
                groupCode = datasetGroupCode;
            } else if (firstCableElement) {
                const cableIndex = parseInt(firstCableElement.textContent.replace('#', '')) - 1;
                if (cableIndex >= 0 && cableIndexMap.has(cableIndex)) {
                    const cableData = cableIndexMap.get(cableIndex);
                    if (cableData && cableData.gruppo) {
                        groupCode = cableData.gruppo;
                    }
                }
            }

            // ✅ MIGLIORATO: Calcolo più preciso delle altezze
            const detailsSection = group.querySelector('.bg-blue-50.rounded-lg.p-6');
            const cableDetails = detailsSection ? detailsSection.querySelectorAll('.space-y-2 > div') : [];

            // Calcola altezza SVG
            const svgs = group.querySelectorAll('.bg-gray-50.rounded-lg.p-4 svg, .bg-gray-50.rounded-lg.p-2 svg');
            let groupHeight = 12; // Header colorato

            let totalSvgHeight = 0;
            for (let j = 0; j < svgs.length; j++) {
                const viewBox = svgs[j].getAttribute('viewBox').split(' ');
                const svgWidth = parseFloat(viewBox[2]);
                const svgHeight = parseFloat(viewBox[3]);
                const scale = (usableWidth - 6) / svgWidth;
                totalSvgHeight += (svgHeight * scale) + 4;
            }

            groupHeight += totalSvgHeight;

            // Calcolo dettagliato altezza tabella
            let detailsHeight = 0;
            if (cableDetails.length > 0) {
                detailsHeight = 12; // Header tabella più spazio

                cableDetails.forEach((cableDetailDiv) => {
                    const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');
                    if (tessera) {
                        const cableIndex = parseInt(tessera.querySelector('span')?.textContent?.replace('#', '') - 1);
                        const cableData = cableIndexMap.get(cableIndex);

                        if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
                            // Cavo multipolare: header + tabella poli
                            detailsHeight += 8; // Header cavo
                            detailsHeight += 6;  // Header tabella
                            detailsHeight += cableData.poli.length * 10; // Ogni polo
                            detailsHeight += 2;  // Separatore
                        } else {
                            detailsHeight += 8;
                        }
                    }
                });
            }

            groupHeight += detailsHeight;

            // ✅ MIGLIORATO: Gestione paginazione intelligente
            const maxGroupHeightPerPage = pageHeight - margin - 60; // Margine di sicurezza

            // Se il gruppo non entra nella pagina corrente, vai alla prossima
            if (currentY + groupHeight > pageHeight - margin) {
                pdf.addPage();
                currentY = margin;

                // Header semplificato per pagine successive
                pdf.setFillColor(...primaryColor);
                pdf.rect(0, 0, pageWidth, 12, 'F');
                pdf.setFontSize(9);
                pdf.setTextColor(255, 255, 255);
                pdf.text(`Schema Cablaggi - ${fileName || 'Senza nome'}`, margin, 8);
                pdf.text(`Pagina ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, 8);
                currentY = 20;
            }

            // ✅ NUOVO: Se il gruppo è ancora troppo alto, dividilo
            if (groupHeight > maxGroupHeightPerPage) {
                // Renderizza solo SVG nella pagina corrente
                const svgOnlyHeight = 12 + totalSvgHeight;

                // Riquadro principale per SVG
                pdf.setFillColor(255, 255, 255);
                pdf.setDrawColor(...lightGrayColor);
                pdf.setLineWidth(0.5);
                pdf.roundedRect(margin, currentY, usableWidth, svgOnlyHeight, 4, 4, 'FD');

                // Header colorato
                pdf.setFillColor(...secondaryColor);
                pdf.roundedRect(margin, currentY, usableWidth, 10, 4, 4, 'F');
                pdf.rect(margin, currentY + 4, usableWidth, 7, 'F');

                pdf.setFontSize(13);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(255, 255, 255);
                pdf.text(actualTitle, margin + 5, currentY + 7);
                
                // Aggiungi il codice del gruppo sulla destra se presente
                if (groupCode) {
                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(255, 255, 255);
                    const groupCodeText = groupCode;
                    const groupCodeWidth = pdf.getTextWidth(groupCodeText);
                    const groupCodeX = margin + usableWidth - groupCodeWidth - 10;
                    const groupCodeY = currentY + 5.5;
                    
                    // Sfondo colorato vivace per il codice del gruppo
                    pdf.setFillColor(10, 186, 181); // #0abab5
                    pdf.roundedRect(groupCodeX - 3, groupCodeY - 2.9, groupCodeWidth + 6, 5, 2, 2, 'F');
                    
                    // Testo del codice
                    pdf.text(groupCodeText, groupCodeX, groupCodeY + 1);
                }

                let innerY = currentY + 13;

                // Renderizza SVG
                for (let j = 0; j < svgs.length; j++) {
                    const svg = svgs[j];
                    const svgClone = createCleanSVGForExport(svg);

                    const viewBox = svg.getAttribute('viewBox').split(' ');
                    const svgWidth = parseFloat(viewBox[2]);
                    const svgHeight = parseFloat(viewBox[3]);
                    const scale = (usableWidth - 6) / svgWidth;
                    const scaledHeight = svgHeight * scale;

                    await pdf.svg(svgClone, {
                        x: margin + 3,
                        y: innerY,
                        width: usableWidth - 6,
                        height: scaledHeight
                    });

                    innerY += scaledHeight + 3;
                }

                // Vai alla pagina successiva per i dettagli (se ci sono)
                if (cableDetails.length > 0) {
                    pdf.addPage();
                    currentY = margin;

                    // Header pagina dettagli
                    pdf.setFillColor(...primaryColor);
                    pdf.rect(0, 0, pageWidth, 12, 'F');
                    pdf.setFontSize(9);
                    pdf.setTextColor(255, 255, 255);
                    pdf.text(`Dettagli ${actualTitle}`, margin, 8);
                    pdf.text(`Pagina ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, 8);
                    currentY = 20;

                    // Renderizza solo la tabella dettagli
                    await renderDetailedCableTable(pdf, cableDetails, currentY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap);
                }

                currentY = margin; // Reset per prossimo gruppo
            } else {
                // ✅ GRUPPO NORMALE: Rendering completo in una pagina
                // Riquadro principale
                pdf.setFillColor(255, 255, 255);
                pdf.setDrawColor(...lightGrayColor);
                pdf.setLineWidth(0.5);
                pdf.roundedRect(margin, currentY, usableWidth, groupHeight, 4, 4, 'FD');

                // Header colorato attaccato
                pdf.setFillColor(...secondaryColor);
                pdf.roundedRect(margin, currentY, usableWidth, 10, 4, 4, 'F');
                pdf.rect(margin, currentY + 4, usableWidth, 7, 'F');

                pdf.setFontSize(13);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(255, 255, 255);
                pdf.text(actualTitle, margin + 5, currentY + 7);
                
                // Aggiungi il codice del gruppo sulla destra se presente
                if (groupCode) {
                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(255, 255, 255);
                    const groupCodeText = groupCode;
                    const groupCodeWidth = pdf.getTextWidth(groupCodeText);
                    const groupCodeX = margin + usableWidth - groupCodeWidth - 10;
                    const groupCodeY = currentY + 5.5;
                    
                    // Sfondo colorato vivace per il codice del gruppo
                    pdf.setFillColor(10, 186, 181); // #0abab5
                    pdf.roundedRect(groupCodeX - 3, groupCodeY - 2.9, groupCodeWidth + 6, 5, 2, 2, 'F');
                    
                    // Testo del codice
                    pdf.text(groupCodeText, groupCodeX, groupCodeY + 1);
                }

                let innerY = currentY + 13;

                // SVG della visualizzazione
                for (let j = 0; j < svgs.length; j++) {
                    const svg = svgs[j];
                    const svgClone = createCleanSVGForExport(svg);

                    const viewBox = svg.getAttribute('viewBox').split(' ');
                    const svgWidth = parseFloat(viewBox[2]);
                    const svgHeight = parseFloat(viewBox[3]);
                    const scale = (usableWidth - 6) / svgWidth;
                    const scaledHeight = svgHeight * scale;

                    await pdf.svg(svgClone, {
                        x: margin + 3,
                        y: innerY,
                        width: usableWidth - 6,
                        height: scaledHeight
                    });

                    // 🔍 DEBUG: Rettangolo per ogni SVG individuale
                    if (settings.debugMode) {
                        pdf.setDrawColor(0, 255, 0); // Verde
                        pdf.setLineWidth(0.3);
                        pdf.rect(margin + 3, innerY, usableWidth - 6, scaledHeight);
                        
                        // Testo con dimensioni SVG
                        pdf.setFontSize(6);
                        pdf.setTextColor(0, 128, 0);
                        pdf.text(`SVG ${j+1}: ${svgHeight.toFixed(1)}px → ${scaledHeight.toFixed(1)}mm`, margin + 5, innerY + 4);
                    }

                    innerY += scaledHeight + 3;
                }

                if (cableDetails.length > 0) {
                    innerY = await renderDetailedCableTable(pdf, cableDetails, innerY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap);
                }

                // 🔍 DEBUG: Rettangoli per altezze totali del gruppo
                if (settings.debugMode) {
                    // Rettangolo per altezza SVG totale (ROSSO)
                    pdf.setDrawColor(255, 0, 0);
                    pdf.setLineWidth(0.5);
                    pdf.rect(margin + 2, currentY + 13, usableWidth - 4, totalSvgHeight);
                    
                    // Testo con altezza SVG stimata
                    pdf.setFontSize(8);
                    pdf.setTextColor(255, 0, 0);
                    pdf.text(`SVG Total: ${totalSvgHeight.toFixed(1)}mm`, margin + 4, currentY + 17);
                    
                    // Rettangolo per altezza totale del gruppo (BLU)
                    pdf.setDrawColor(0, 0, 255);
                    pdf.rect(margin + 1, currentY + 13, usableWidth - 2, groupHeight - 13);
                    
                    // Testo con altezza totale
                    pdf.setTextColor(0, 0, 255);
                    pdf.text(`Group Total: ${groupHeight.toFixed(1)}mm`, margin + 4, currentY + 21);
                }

                currentY += groupHeight + 8;
            }
        }

        // FOOTER
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            pdf.setDrawColor(...lightGrayColor);
            pdf.setLineWidth(0.5);
            pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

            pdf.setFontSize(8);
            pdf.setTextColor(...grayColor);
            pdf.text('Generato con EW-3035+L Cable Manager', margin, pageHeight - 8);
            pdf.text(`Pagina ${i} di ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        }

        // Ripristina elementi nascosti
        elementsToHide.forEach(({ element, display }) => {
            if (display !== undefined) element.style.display = display;
        });

        // Salva PDF
        const pdfFileName = settings.fileName ?
            getFullFileName(settings, 'pdf', false, false).replace('.pdf', '-schema-colori.pdf') :
            'schema-cablaggi-colori.pdf';

        pdf.save(pdfFileName);
        setSuccessMessage(t('fileOperations.pdfExportedSuccess', { name: pdfFileName }));
        setTimeout(() => setSuccessMessage(''), 5000);

        settings.dynamicGroupsDivision = originalDynamicGroupsDivision;

        if (setDynamicGroupsDivision) {
            setDynamicGroupsDivision(originalDynamicGroupsDivision);
        }

    } catch (error) {
        console.error('Errore esportazione PDF:', error);
        setWarnings(prev => [...prev, t('fileOperations.exportPDFError', { error: error.message })]);
        
        settings.dynamicGroupsDivision = originalDynamicGroupsDivision;

        if (setDynamicGroupsDivision) {
            setDynamicGroupsDivision(originalDynamicGroupsDivision);
        }
    }
};

export const exportSchematicToPDFColor = async (cables, settings, setWarnings, setSuccessMessage, setDynamicGroupsDivision = null, debugMode = false) => {
    return exportSchematicToPDFBase({
        cables,
        settings,
        setWarnings,
        setSuccessMessage,
        setDynamicGroupsDivision,
        debugMode,
        groupSelector: '[id^="cable-group-"]'
    });
};

export const exportSchematicNestedToPDF = async (cables, settings, setWarnings, setSuccessMessage, setDynamicGroupsDivision = null, debugMode = false) => {
    return exportSchematicToPDFBase({
        cables,
        settings,
        setWarnings,
        setSuccessMessage,
        setDynamicGroupsDivision,
        debugMode,
        groupSelector: '[data-nested-node-id]'
    });
};

// Rendering tabella dettagliata
const renderDetailedCableTable = async (pdf, cableDetails, startY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap) => {
    let currentY = startY;

    // Separatore elegante
    pdf.setDrawColor(...lightGrayColor);
    pdf.setLineWidth(0.3);
    pdf.line(margin + 10, currentY, margin + usableWidth - 10, currentY);
    currentY += 5;

    // Titolo sezione con sfondo colorato
    pdf.setFillColor(...primaryColor);
    pdf.roundedRect(margin + 3, currentY - 2, 60, 6, 2, 2, 'F');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('Dettagli Tecnici Completi', margin + 6, currentY + 2);
    currentY += 8;

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');

    cableDetails.forEach((cableDetailDiv, idx) => {
        const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');

        if (tessera) {
            // Alternanza colori righe - più delicata
            if (idx % 2 === 0) {
                pdf.setFillColor(248, 250, 252); // bg-slate-50
                pdf.roundedRect(margin + 3, currentY - 2, usableWidth - 6, 0, 2, 2, 'F'); // Altezza dinamica
            }

            // Badge numero - più grande e colorato
            const numberBadge = tessera.querySelector('span[class*="px-2"][class*="py-0.5"]');
            const numberText = numberBadge ? numberBadge.textContent.trim() : '';

            if (numberText) {
                const cableIndex = parseInt(numberText.replace('#', '')) - 1;
                const cableData = cableIndexMap.get(cableIndex);

                if (cableData) {
                    // Badge numero migliorato
                pdf.setFillColor(...primaryColor);
                pdf.roundedRect(margin + 5, currentY - 1, 15, 6, 2, 2, 'F');
                pdf.setTextColor(255, 255, 255);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(9);
                pdf.text(numberText, margin + 12.5, currentY + 3, { align: 'center' });

                pdf.setTextColor(0, 0, 0);
                pdf.setFont('helvetica', 'normal');

                if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
                    // ✅ CAVO MULTIPOLARE - LAYOUT OTTIMIZZATO

                    // Titolo cavo
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(10);
                    let mainTitle = `${cableData.nomeCavo || 'Cavo Multipolare'}`;

                    // Aggiungi siglatura se presente
                    let siglaturaText = '';
                    if (!settings.marcaturePerCollegamento) {
                        siglaturaText = '';
                    } else {
                        try {
                            const siglature = new Set();
                            cableData.poli.forEach(polo => {
                                try {
                                    const sigA = extractSiglaturaFromCollegamento(polo.collegamentoA);
                                    const sigB = extractSiglaturaFromCollegamento(polo.collegamentoB);
                                    if (sigA && String(sigA).trim() !== '') siglature.add(String(sigA).trim());
                                    if (sigB && String(sigB).trim() !== '') siglature.add(String(sigB).trim());
                                } catch (e) {
                                    console.warn('Errore estrazione siglatura polo:', e);
                                }
                            });
                            if (siglature.size > 0) {
                                siglaturaText = ` - Sig: ${Array.from(siglature).join(', ')}`;
                            }
                        } catch (e) {
                            console.warn('Errore estrazione siglature multipolari:', e);
                            siglaturaText = '';
                        }
                    }

                    pdf.text(`${mainTitle}${siglaturaText}`, margin + 25, currentY + 3);

                    // ✅ RIGA INFO GENERALI - Include sezione se uniforme
                    const sezioni = [...new Set(cableData.poli.map(p => p.sezione))];
                    const hasUniformSection = sezioni.length === 1;

                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);
                    let infoGenerali = `${cableData.poli.length} poli - ${cableData.lunghezza}mm`;

                    if (hasUniformSection) {
                        infoGenerali += ` - ${formatSezione(sezioni[0], settings.useAWG)}`;
                    }

                    pdf.text(infoGenerali, margin + 40, currentY + 3);
                    currentY += 8;

                    // ✅ HEADER TABELLA OTTIMIZZATO - Con o senza sezione
                    const headerY = currentY;
                    pdf.setFillColor(...lightGrayColor);
                    pdf.roundedRect(margin + 20, headerY - 1, usableWidth - 25, 6, 1, 1, 'F');

                    pdf.setFontSize(7);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(...grayColor);

                    // ✅ COLONNE OTTIMIZZATE - Con siglature invece della sezione se uniforme
                    let cols;
                    if (hasUniformSection) {
                        // Layout con siglature (senza sezione)
                        cols = {
                            polo: margin + 25,
                            colore: margin + 40,
                            sigA: margin + 55,
                            termA: margin + 70,
                            lunghA: margin + 85,
                            collA: margin + 100,
                            sigB: margin + 120,
                            termB: margin + 135,
                            lunghB: margin + 150,
                            collB: margin + 165
                        };

                        pdf.text('Polo', cols.polo, headerY + 3);
                        pdf.text('Colore', cols.colore, headerY + 3);
                        pdf.text('Sig.A', cols.sigA, headerY + 3);
                        pdf.text('Term.A', cols.termA, headerY + 3);
                        pdf.text('Spel.A', cols.lunghA, headerY + 3);
                        pdf.text('Coll.A', cols.collA, headerY + 3);
                        pdf.text('Sig.B', cols.sigB, headerY + 3);
                        pdf.text('Term.B', cols.termB, headerY + 3);
                        pdf.text('Spel.B', cols.lunghB, headerY + 3);
                        pdf.text('Coll.B', cols.collB, headerY + 3);
                    } else {
                        // Layout originale con sezione
                        cols = {
                            polo: margin + 28,
                            colore: margin + 40,
                            sezione: margin + 60,
                            sigA: margin + 60,
                            termA: margin + 78,
                            lunghA: margin + 93,
                            collA: margin + 108,
                            sigB: margin + 75,
                            termB: margin + 135,
                            lunghB: margin + 150,
                            collB: margin + 165
                        };

                        pdf.text('Polo', cols.polo, headerY + 3);
                        pdf.text('Colore', cols.colore, headerY + 3);
                        pdf.text('Sez.', cols.sezione, headerY + 3);
                        pdf.text('Sig.A', cols.sigA, headerY + 3);
                        pdf.text('Term.A', cols.termA, headerY + 3);
                        pdf.text('Spel.A', cols.lunghA, headerY + 3);
                        pdf.text('Coll.A', cols.collA, headerY + 3);
                        pdf.text('Sig.B', cols.sigB, headerY + 3);
                        pdf.text('Term.B', cols.termB, headerY + 3);
                        pdf.text('Spel.B', cols.lunghB, headerY + 3);
                        pdf.text('Coll.B', cols.collB, headerY + 3);
                    }

                    currentY += 8;

                    // Righe poli
                    pdf.setFont('helvetica', 'normal');
                    pdf.setTextColor(0, 0, 0);

                    cableData.poli.forEach((polo, poloIdx) => {
                        // Alternanza colori ogni polo
                        if (poloIdx % 2 === 1) {
                            pdf.setFillColor(250, 250, 250);
                            pdf.rect(margin + 25, currentY - 2, usableWidth - 30, 8, 'F');
                        }

                        // Funzione per pulire testi - UGUALE ALLA VERSIONE B/N
                        const cleanText = (text) => {
                            if (!text) return '';
                            return String(text)
                                .replace(/[▲▼→←↔]/g, '') // Rimuove frecce
                                .replace(/['']/g, "'")    // Sostituisce apostrofi strani
                                .replace(/[""]/g, '"')    // Sostituisce virgolette strane
                                .replace(/[^\x20-\x7E]/g, '?') // Sostituisce caratteri non ASCII con ?
                                .trim();
                        };

                        pdf.setFontSize(7);
                        pdf.text(`P${poloIdx + 1}`, cols.polo, currentY + 2);
                        pdf.text(cleanText(polo.colore), cols.colore, currentY + 2);

                        if (hasUniformSection) {
                            // ✅ LAYOUT CON SIGLATURE
                            pdf.text(cleanText(polo.siglaturaA), cols.sigA, currentY + 2);
                            pdf.text(cleanText(polo.siglaturaB), cols.sigB, currentY + 2);
                            pdf.text(cleanText(polo.terminaleA), cols.termA, currentY + 2);
                            pdf.text(`${polo.lunghezzaA || 0}`, cols.lunghA, currentY + 2);

                            const collA = cleanText(polo.collegamentoA);
                            pdf.text(collA.length > 10 ? collA.substring(0, 10) + '...' : collA, cols.collA, currentY + 2);
                            pdf.text(cleanText(polo.terminaleB), cols.termB, currentY + 2);
                            pdf.text(`${polo.lunghezzaB || 0}`, cols.lunghB, currentY + 2);

                            const collB = cleanText(polo.collegamentoB);
                            pdf.text(collB.length > 10 ? collB.substring(0, 10) + '...' : collB, cols.collB, currentY + 2);
                        } else {
                            // Layout originale con sezione
                            pdf.text(`${polo.sezione}${settings.useAWG ? 'AWG' : 'mm²'}`, cols.sezione, currentY + 2);
                            pdf.text(cleanText(polo.terminaleA), cols.termA, currentY + 2);
                            pdf.text(`${polo.lunghezzaA || 0}`, cols.lunghA, currentY + 2);

                            const collA = cleanText(polo.collegamentoA);
                            pdf.text(collA.length > 12 ? collA.substring(0, 12) + '...' : collA, cols.collA, currentY + 2);
                            pdf.text(cleanText(polo.terminaleB), cols.termB, currentY + 2);
                            pdf.text(`${polo.lunghezzaB || 0}`, cols.lunghB, currentY + 2);

                            const collB = cleanText(polo.collegamentoB);
                            pdf.text(collB.length > 12 ? collB.substring(0, 12) + '...' : collB, cols.collB, currentY + 2);
                        }

                        currentY += 8;
                    });

                    currentY += 2; // Spazio dopo tabella

                } else {
                    // ✅ CAVO STANDARD - UNA RIGA COMPLETA E BEN SPAZIATA

                    if (cableData) {
                        // Funzione per pulire testi - UGUALE ALLA VERSIONE B/N
                        const cleanText = (text) => {
                            if (!text) return '—';
                            return String(text)
                                .replace(/[▲▼→←↔]/g, '') // Rimuove frecce
                                .replace(/['']/g, "'")    // Sostituisce apostrofi strani
                                .replace(/[""]/g, '"')    // Sostituisce virgolette strane
                                .replace(/[^\x20-\x7E]/g, '?') // Sostituisce caratteri non ASCII con ?
                                .trim();
                        };

                        // ✅ CALCOLA SIGLATURA
                        let siglaturaDisplay = '';
                        if (!settings.marcaturePerCollegamento) {
                            if (cableData.siglatura && String(cableData.siglatura).trim() !== '') {
                                siglaturaDisplay = cleanText(cableData.siglatura);
                            } else {
                                siglaturaDisplay = '—';
                            }
                        } else {
                            try {
                                const siglature = getSiglaturaPerStampa(cableData, settings);
                                const sigArray = Array.isArray(siglature) ? siglature : [siglature];
                                const validSigs = sigArray.filter(sig => sig && String(sig).trim() !== '').map(cleanText);
                                siglaturaDisplay = validSigs.length > 0 ? validSigs.join(',') : '—';
                            } catch (e) {
                                console.warn('Errore estrazione siglatura:', e);
                                siglaturaDisplay = '—';
                            }
                        }

                        // ✅ STAMPA SIGLATURA IN GRASSETTO
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(10);
                        pdf.text(siglaturaDisplay, margin + 25, currentY + 3);

                        // ✅ CALCOLA POSIZIONE DINAMICA PER IL RESTO DELLE INFO
                        const siglaturaWidth = pdf.getTextWidth(siglaturaDisplay);
                        const infoStartX = margin + 25 + siglaturaWidth + 6; // 8px di spazio dopo siglatura

                        // ✅ STRINGA CON LE INFO (senza siglatura, frecce ASCII)
                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(8);
                        const infoCompleta = `${cleanText(cableData.colore)}  •  ${formatSezione(cableData.sezione, settings.useAWG)}  •  ${cableData.lunghezza}mm  •  ${cleanText(cableData.terminaleA)} -> ${cleanText(cableData.terminaleB)} | ${cleanText(cableData.collegamentoA)} -> ${cleanText(cableData.collegamentoB)}`;

                        // Calcola larghezza disponibile per le info
                        const availableWidth = usableWidth - (infoStartX - margin) - 10;
                        const lines = pdf.splitTextToSize(infoCompleta, availableWidth);

                        // Stampa massimo 2 righe
                        const maxLines = Math.min(lines.length, 2);
                        for (let i = 0; i < maxLines; i++) {
                            pdf.text(lines[i], infoStartX, currentY + 3 + (i * 8));
                        }

                        if (lines.length > 2) {
                            // Indica che c'è altro testo
                            pdf.setFontSize(6);
                            pdf.setTextColor(100, 100, 100);
                            pdf.text('...', margin + usableWidth - 15, currentY + 3 + (maxLines * 8));
                            pdf.setTextColor(0, 0, 0);
                            pdf.setFontSize(8);
                        }

                        currentY += 6; // Spazio dinamico
                    }
                }
                }

                // Riga di separazione delicata tra cavi
                if (idx < cableDetails.length - 1) {
                    pdf.setDrawColor(240, 240, 240);
                    pdf.setLineWidth(0.2);
                    pdf.line(margin + 10, currentY, margin + usableWidth - 10, currentY);
                    currentY += 2; // Spazio ridotto
                }
            }
        }
    });

    return currentY + 3; // Ritorna la nuova posizione Y
};

// ==================== FUNZIONE CLEAN SVG PER EXPORT ====================

const createCleanSVGForExport = (originalSvg) => {
    const clone = originalSvg.cloneNode(true);

    const tooltips = clone.querySelectorAll('g:last-child');
    tooltips.forEach(g => {
        const rect = g.querySelector('rect[rx="5"]');
        const text = g.querySelector('text.text-xs.fill-gray-700');
        if (rect && text && text.textContent.includes('•')) {
            g.style.opacity = '1';
            g.style.display = 'block';
            g.removeAttribute('display');

            rect.style.opacity = '1';
            text.style.opacity = '1';
        }
    });

    clone.querySelectorAll('text').forEach(textEl => {
        const className = textEl.getAttribute('class') || '';

        // ✅ SOSTITUISCI FRECCE SOLO NEL TESTO PRINCIPALE, NON NEI TSPAN
        // Se il text non ha tspan figli, applica la sostituzione direttamente
        const tspans = textEl.querySelectorAll('tspan');
        if (tspans.length === 0 && textEl.textContent) {
            textEl.textContent = replaceArrowsForPDF(textEl.textContent);
        } else if (tspans.length > 0) {
            // Se ha tspan, cerca le frecce solo nel primo tspan (di solito la linea principale)
            tspans.forEach((tspan, index) => {
                if (tspan.textContent && (tspan.textContent.includes('▲') || tspan.textContent.includes('▼'))) {
                    tspan.textContent = replaceArrowsForPDF(tspan.textContent);
                }
            });
        }

        // Applica stili
        if (className.includes('fill-blue-800')) {
            textEl.setAttribute('fill', '#1e40af');
        } else if (className.includes('fill-gray-700')) {
            textEl.setAttribute('fill', '#374151');
        } else if (className.includes('fill-gray-600')) {
            textEl.setAttribute('fill', '#4b5563');
        } else if (className.includes('fill-gray-800')) {
            textEl.setAttribute('fill', '#1f2937');
        }

        if (className.includes('font-bold')) {
            textEl.setAttribute('font-weight', 'bold');
        } else if (className.includes('font-semibold')) {
            textEl.setAttribute('font-weight', '600');
        } else if (className.includes('font-medium')) {
            textEl.setAttribute('font-weight', '500');
        }

        if (className.includes('text-sm')) {
            textEl.setAttribute('font-size', '14px');
        } else if (className.includes('text-xs')) {
            textEl.setAttribute('font-size', '12px');
        }

        textEl.style.opacity = '1';
        textEl.style.display = 'block';
    });

    // ✅ GESTISCI I TSPAN SEPARATAMENTE (NON TOCCARE IL CONTENUTO, SOLO GLI STILI)
    clone.querySelectorAll('tspan').forEach(tspanEl => {
        // NON MODIFICARE IL CONTENUTO DEI TSPAN - mantieni la struttura multi-linea
        // Solo assicurati che siano visibili
        tspanEl.style.opacity = '1';
        tspanEl.style.display = 'inline';
    });

    // ✅ RIMUOVI CLASSI ANIMATE
    clone.querySelectorAll('.cable-path').forEach(path => {
        path.classList.remove('animated');
    });

    return clone;
};